import React from 'react';
import { useAcceptMatch, useRejectMatch } from '../../hooks/useJobs';
import { useAuth } from '../../hooks/useAuth';
import './AcceptRejectButtons.css';

const AcceptRejectButtons = ({ match, job, onActionComplete }) => {
  const { user } = useAuth();
  const userRole = user?.role_name;
  
  const acceptMatchMutation = useAcceptMatch();
  const rejectMatchMutation = useRejectMatch();

  // Only employees can accept/reject matches
  // Employers don't need to accept - when employee accepts, it's automatically confirmed
  if (userRole === 'employer') {
    // For employers, just show the match status
    const matchStatus = match?.status || 'pending';
    
    if (matchStatus === 'accepted') {
      return (
        <div className="accept-reject-buttons">
          <div className="accept-reject-buttons__status accept-reject-buttons__status--accepted">
            <svg className="accept-reject-buttons__icon" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Job Confirmed - Employee Accepted</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="accept-reject-buttons">
        <div className="accept-reject-buttons__status accept-reject-buttons__status--waiting">
          <svg className="accept-reject-buttons__icon" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          <span>Waiting for Employee Response</span>
        </div>
      </div>
    );
  }

  // For employees: show accept/reject buttons
  const matchStatus = match?.status || 'pending';
  const isPending = matchStatus === 'pending';
  const isAccepted = matchStatus === 'accepted';
  const isRejected = matchStatus === 'rejected' || matchStatus === 'discarded';

  const handleAccept = async () => {
    try {
      await acceptMatchMutation.mutateAsync({
        matchId: match.id,
        userRole: userRole
      });
      
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error accepting match:', error);
    }
  };

  const handleReject = async () => {
    try {
      await rejectMatchMutation.mutateAsync({
        matchId: match.id,
        userRole: userRole
      });
      
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error rejecting match:', error);
    }
  };

  if (isAccepted) {
    return (
      <div className="accept-reject-buttons">
        <div className="accept-reject-buttons__status accept-reject-buttons__status--accepted">
          <svg className="accept-reject-buttons__icon" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Job Confirmed - You and the employer have been notified</span>
        </div>
      </div>
    );
  }

  if (isRejected) {
    return (
      <div className="accept-reject-buttons">
        <div className="accept-reject-buttons__status accept-reject-buttons__status--rejected">
          <svg className="accept-reject-buttons__icon" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>You Rejected This Match</span>
        </div>
      </div>
    );
  }


  return (
    <div className="accept-reject-buttons">
      <button
        className="accept-reject-buttons__button accept-reject-buttons__button--accept"
        onClick={handleAccept}
        disabled={acceptMatchMutation.isPending || rejectMatchMutation.isPending}
      >
        {acceptMatchMutation.isPending ? (
          <>
            <span className="accept-reject-buttons__spinner"></span>
            Accepting...
          </>
        ) : (
          <>
            <svg className="accept-reject-buttons__icon" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Accept
          </>
        )}
      </button>
      <button
        className="accept-reject-buttons__button accept-reject-buttons__button--reject"
        onClick={handleReject}
        disabled={acceptMatchMutation.isPending || rejectMatchMutation.isPending}
      >
        {rejectMatchMutation.isPending ? (
          <>
            <span className="accept-reject-buttons__spinner"></span>
            Rejecting...
          </>
        ) : (
          <>
            <svg className="accept-reject-buttons__icon" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            Reject
          </>
        )}
      </button>
    </div>
  );
};

export default AcceptRejectButtons;











